import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { Factory, Plus, RefreshCw, Search } from 'lucide-react';
import {
    useBulkUpdateFishingBrands,
    useFishingBrandFacets,
    useFishingBrandsList,
} from './hooks/useFishingBrandsAdmin';
import {
    FISHING_BRAND_CATEGORIES,
    FISHING_BRAND_STATUSES,
    type FishingBrandStatus,
} from './types';

const PAGE_SIZE = 50;

const STATUS_STYLE: Record<FishingBrandStatus, string> = {
    publicada: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    borrador: 'bg-muted text-muted-foreground',
    revisar: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
};

function BrandLogoThumb({
    src,
    name,
    dark,
}: {
    src: string | null;
    name: string;
    dark: boolean;
}) {
    if (!src) {
        return (
            <div className="h-10 w-10 rounded-lg border border-dashed border-border bg-muted/40 flex items-center justify-center text-[10px] text-muted-foreground">
                —
            </div>
        );
    }
    return (
        <div
            className={cn(
                'h-10 w-10 rounded-lg border border-border flex items-center justify-center overflow-hidden',
                dark ? 'bg-[#172A40]' : 'bg-card',
            )}
        >
            <img src={src} alt={name} className="max-h-8 max-w-8 object-contain" />
        </div>
    );
}

export function BrandsPage() {
    const navigate = useNavigate();
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [categorySlug, setCategorySlug] = useState('all');
    const [countryIso, setCountryIso] = useState('all');
    const [status, setStatus] = useState('all');
    const [page, setPage] = useState(0);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkCategory, setBulkCategory] = useState<string>(FISHING_BRAND_CATEGORIES[0].label);

    useEffect(() => {
        const t = window.setTimeout(() => setSearch(searchInput), 300);
        return () => window.clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        setPage(0);
    }, [search]);

    const { data, isLoading, refetch } = useFishingBrandsList({
        search: search || undefined,
        categorySlug,
        countryIso,
        status,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
    });
    const { data: facets } = useFishingBrandFacets();
    const bulk = useBulkUpdateFishingBrands();

    const rows = data?.rows ?? [];
    const total = data?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const countries = useMemo(
        () => (facets ?? []).map((f) => [f.country_iso, f.country_name] as const),
        [facets],
    );

    const toggle = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const runBulk = async (action: 'publicar' | 'borrador' | 'categoria') => {
        if (selected.size === 0) return;
        await bulk.mutateAsync({
            ids: [...selected],
            action,
            category: action === 'categoria' ? bulkCategory : undefined,
        });
        setSelected(new Set());
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Marcas de pesca"
                description="Catálogo editorial. Las altas manuales desde club aparecen en estado «Revisar» (origen Alta club) para publicar o retirar."
            >
                <button
                    type="button"
                    onClick={() => navigate('/brands/new')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                >
                    <Plus size={14} />
                    Nueva marca
                </button>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors"
                >
                    <RefreshCw size={14} />
                    Actualizar
                </button>
            </PageHeader>

            <div className="space-y-3">
                <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Buscar por nombre o slug…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                    />
                </div>
                <div className="flex flex-wrap gap-2">
                    <select
                        value={categorySlug}
                        onChange={(e) => {
                            setCategorySlug(e.target.value);
                            setPage(0);
                        }}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    >
                        <option value="all">Todas las categorías</option>
                        {FISHING_BRAND_CATEGORIES.map((c) => (
                            <option key={c.slug} value={c.slug}>{c.label}</option>
                        ))}
                    </select>
                    <select
                        value={countryIso}
                        onChange={(e) => {
                            setCountryIso(e.target.value);
                            setPage(0);
                        }}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    >
                        <option value="all">Todos los países</option>
                        {countries.map(([iso, name]) => (
                            <option key={iso} value={iso}>{name}</option>
                        ))}
                    </select>
                    <select
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            setPage(0);
                        }}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    >
                        <option value="all">Todos los estados</option>
                        {FISHING_BRAND_STATUSES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                        <option value="club_manual">Alta desde club</option>
                        <option value="sin_logo">Sin logo</option>
                    </select>
                    <button
                        type="button"
                        onClick={() => {
                            setStatus('revisar');
                            setPage(0);
                        }}
                        className={cn(
                            'h-9 rounded-md border px-3 text-sm font-medium transition-colors',
                            status === 'revisar'
                                ? 'border-amber-500/50 bg-amber-500/15 text-amber-900 dark:text-amber-200'
                                : 'border-border bg-background text-muted-foreground hover:text-foreground',
                        )}
                    >
                        Pendientes de revisión
                    </button>
                    <span className="self-center text-xs text-muted-foreground">
                        {total} marca{total !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>

            {selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3 text-sm">
                    <span className="text-muted-foreground">{selected.size} seleccionadas</span>
                    <button
                        type="button"
                        className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium"
                        onClick={() => void runBulk('publicar')}
                    >
                        Publicar
                    </button>
                    <button
                        type="button"
                        className="px-3 py-1.5 rounded-md bg-muted text-foreground text-xs font-medium"
                        onClick={() => void runBulk('borrador')}
                    >
                        Pasar a borrador
                    </button>
                    <select
                        value={bulkCategory}
                        onChange={(e) => setBulkCategory(e.target.value)}
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                    >
                        {FISHING_BRAND_CATEGORIES.map((c) => (
                            <option key={c.slug} value={c.label}>{c.label}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="px-3 py-1.5 rounded-md border border-border text-xs font-medium"
                        onClick={() => void runBulk('categoria')}
                    >
                        Cambiar categoría
                    </button>
                </div>
            )}

            {isLoading ? (
                <div className="rounded-xl border border-border bg-card h-64 animate-pulse" />
            ) : rows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center">
                    <Factory size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm text-muted-foreground">No hay marcas con estos filtros</p>
                </div>
            ) : (
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border bg-muted/40">
                                    <th className="w-10 px-3 py-3" />
                                    <th className="text-left font-medium px-3 py-3">Logo</th>
                                    <th className="text-left font-medium px-3 py-3">Nombre</th>
                                    <th className="text-left font-medium px-3 py-3">Categoría</th>
                                    <th className="text-left font-medium px-3 py-3">País</th>
                                    <th className="text-left font-medium px-3 py-3">Web</th>
                                    <th className="text-left font-medium px-3 py-3">Estado</th>
                                    <th className="text-left font-medium px-3 py-3">Origen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer"
                                        onClick={() => navigate(`/brands/${row.id}`)}
                                    >
                                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selected.has(row.id)}
                                                onChange={() => toggle(row.id)}
                                            />
                                        </td>
                                        <td className="px-3 py-2">
                                            <BrandLogoThumb
                                                src={row.logo_url}
                                                name={row.name}
                                                dark={row.logo_background === 'oscuro'}
                                            />
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-foreground">{row.name}</div>
                                            <div className="text-[11px] text-muted-foreground">{row.slug}</div>
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground">{row.category}</td>
                                        <td className="px-3 py-2 text-muted-foreground">{row.country_name}</td>
                                        <td className="px-3 py-2">
                                            {row.website_url ? (
                                                <a
                                                    href={row.website_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-primary hover:underline"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    Web
                                                </a>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={cn('text-[11px] px-2 py-0.5 rounded-full', STATUS_STYLE[row.status])}>
                                                {FISHING_BRAND_STATUSES.find((s) => s.value === row.status)?.label ?? row.status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            {row.submission_source === 'club_manual' ? (
                                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-800 dark:text-sky-300">
                                                    Alta club
                                                </span>
                                            ) : row.submission_source === 'import' ? (
                                                <span className="text-[11px] text-muted-foreground">Import</span>
                                            ) : (
                                                <span className="text-[11px] text-muted-foreground">Admin</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {pageCount > 1 && (
                <div className="flex items-center justify-between text-sm">
                    <button
                        type="button"
                        disabled={page === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        className="px-3 py-1.5 rounded-md border border-border disabled:opacity-40"
                    >
                        Anterior
                    </button>
                    <span className="text-muted-foreground">
                        Página {page + 1} de {pageCount}
                    </span>
                    <button
                        type="button"
                        disabled={page + 1 >= pageCount}
                        onClick={() => setPage((p) => p + 1)}
                        className="px-3 py-1.5 rounded-md border border-border disabled:opacity-40"
                    >
                        Siguiente
                    </button>
                </div>
            )}
        </div>
    );
}
